import { DownloadFilterCriteria, toExpiryDateFilters, ExpiryDateType } from '../models/download-filter.model';
import { utils } from './utils';

/**
 * Build expiry date download filter criteria from form values.
 * Shared across material-rate and labour-rate filter components.
 */
export function buildExpiryDateDownloadFilters(
  fieldName: string,
  expiryDateType: string | null,
  expiryDate?: string | null,
  expiryDateFrom?: string | null,
  expiryDateTo?: string | null,
): DownloadFilterCriteria[] {
  if (!expiryDateType) return [];

  if (expiryDateType === 'BETWEEN') {
    if (!expiryDateFrom || !expiryDateTo) return [];
    return toExpiryDateFilters(
      fieldName,
      expiryDateType as ExpiryDateType,
      utils.convertDateStringToUTC(expiryDateFrom, false, true),
      utils.convertDateStringToUTC(expiryDateTo, false, false),
    );
  }

  if (!expiryDate) return [];
  return toExpiryDateFilters(fieldName, expiryDateType as ExpiryDateType, utils.convertDateStringToUTC(expiryDate, false, true));
}
/**
 * Shared helper functions for filter components to reduce code duplication
 */

export type FilterValue = string | string[] | boolean | boolean[] | undefined | null | any[];

/**
 * Get the display label for expiry date filter types
 */
export function getExpiryTypeLabel(type: string | null): string {
  switch (type) {
    case 'EQUALS':
      return 'Equals';
    case 'ON_OR_BEFORE':
      return 'On or before';
    case 'ON_OR_AFTER':
      return 'On or after';
    case 'BETWEEN':
      return 'Between';
    case 'BEFORE':
      return 'Before';
    case 'AFTER':
      return 'After';
    default:
      return '';
  }
}

/**
 * Check if a filter value is empty (null, undefined, empty string, empty array)
 */
export function isFilterValueEmpty(value: FilterValue): boolean {
  return value === null || value === undefined || value === '' || (Array.isArray(value) && value.length === 0);
}

/**
 * Check if a filter value is valid (not empty)
 */
export function isFilterValueValid(value: FilterValue): boolean {
  return !isFilterValueEmpty(value);
}

/**
 * Mark a filter as tagged if it has a valid value
 * @returns true if filter was added, false otherwise
 */
export function markAsTagged(taggedFilters: { key: string; value: FilterValue }[], key: string, newValue: FilterValue): boolean {
  // Remove existing filter with same key
  removeFilter(taggedFilters, key);

  // Add if value is valid
  if (isFilterValueValid(newValue)) {
    taggedFilters.push({ key, value: newValue });
    return true;
  }
  return false;
}

/**
 * Remove a filter from the tagged filters list
 */
export function removeFilter(taggedFilters: { key: string; value: FilterValue }[], key: string): void {
  const index = taggedFilters.findIndex((f) => f.key === key);
  if (index !== -1) {
    taggedFilters.splice(index, 1);
  }
}

/**
 * Get the value of a filter by key
 */
export function getFilterValue(taggedFilters: { key: string; value: FilterValue }[], key: string): FilterValue {
  const filter = taggedFilters.find((f) => f.key === key);
  return filter ? filter.value : null;
}

/**
 * Check if a value is an array type
 */
export function isArrayType(value: FilterValue): boolean {
  return Array.isArray(value);
}

/**
 * Build expiry date filter tag label
 */
export function buildExpiryDateLabel(
  expiryDateType: string | null,
  expiryDate?: string | null,
  expiryDateFrom?: string | null,
  expiryDateTo?: string | null,
): string {
  if (!expiryDateType) return '';

  if (expiryDateType === 'BETWEEN') {
    if (expiryDateFrom && expiryDateTo) {
      return `Between ${expiryDateFrom} and ${expiryDateTo}`;
    }
  } else if (expiryDate) {
    const typeLabel = getExpiryTypeLabel(expiryDateType);
    return `${typeLabel} ${expiryDate}`;
  }

  return '';
}

/**
 * Remove an item from an array by index
 */
export function removeAtIndex<T>(array: T[], index: number): T[] {
  if (index < 0 || index >= array.length) return array;
  return array.filter((_, i) => i !== index);
}

/**
 * Remove an item from an array by value
 */
export function removeByValue<T>(array: T[], value: T): T[] {
  return array.filter((item) => item !== value);
}
